import { createSlice } from '@reduxjs/toolkit';
import { find } from 'lodash';

import socket from '../../utils/socket';

const initialState = {
  isLoading: false,
  error: false,
  // datas
  notifications: [],
  notifCounts: {
    total: 0,
    enabled: 0
  }
};

const slice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getNotifications(state, action) {
      const { notifications } = action.payload;
      state.isLoading = false;
      state.notifications = notifications;
    },

    addNotification(state, action) {
      const notification = find(state.notifications, (el) => el.id === action.payload.id);
      if (!notification) {
        state.notifications.unshift(action.payload);
      }
    },

    /**
     * Met à jour dans le state Redux le nombre de sites accessibles
     * et le nombre de sites avec notifications activées pour l'utilisateur.
     *
     * @param {{ total: number, enabled: number }} action.payload - Données reçues de l'API
     */
    setNotifCounts(state, action) {
      const { total, enabled } = action.payload;
      state.isLoading = false;
      state.notifCounts = { total, enabled };
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { addNotification } = slice.actions;

export function getNotifications() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/notifications');
      dispatch(slice.actions.getNotifications(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Active/désactive les notifications pour un utilisateur sur un site donné.
 * (création ou suppression dans user_notif_clocking).
 *
 * @param {Object} params - Paramètres pour la mise à jour.
 * @param {boolean} params.enableNotification - true pour activer, false pour désactiver.
 * @param {number} params.siteId - ID du site concerné.
 * @param {number} params.userId - ID de l'utilisateur concerné.
 * @param {Function} [params.callback] - Fonction optionnelle appelée après succès, avec `enableNotification` comme argument.
 * @returns {Promise<void>}
 */
export function updateNotifOnSite({ enableNotification, siteId, userId, callback }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      if (enableNotification) {
        await socket.post('/notifications/createSite', { siteId, userId });
      } else {
        await socket.delete('/notifications/deleteSite', { siteId, userId });
      }
      callback?.(enableNotification);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      callback?.(!enableNotification);
    } finally {
      dispatch(slice.actions.stopLoading());
    }
  };
}

export function sendNotification({ title = 'Nouveau message', message, resourceId, contractId }) {
  return async (dispatch) => {
    try {
      await socket.post('/notifications/send', {
        title,
        message,
        resourceId,
        contractId
      });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Met à jour les préférences de notifications de l'utilisateur.
 *
 * Envoie une requête PUT via socket vers l'endpoint `/notifications/preferences`
 * avec les données fournies dans le payload.
 *
 * @param {Object} payload - Données contenant les nouvelles préférences de notifications.
 * @returns {Function} Fonction asynchrone retournant le résultat de la requête ou une erreur.
 *
 * @example
 * const payload = { user_id: 1, notify_anomaly_clocking_push_mode: 1 };
 * dispatch(updateNotificationsPreferences(payload));
 */
export function updateNotificationsPreferences(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.put('/notifications/preferences', payload);
      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error: error.message };
    }
  };
}

/**
 * Récupère le nombre total de sites accessibles et
 * le nombre de sites pour lesquels les notifications sont activées.
 *
 * Effectue un appel GET vers `/notifications/sites/counts/{userId}`.
 * Met à jour le state `notifCounts` dans Redux.
 *
 * @param {number} userId - ID de l'utilisateur
 * @returns {Function} Fonction Redux async (thunk)
 */
export function getNotifSitesCounts(userId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get(`/notifications/sites/counts/${userId}`);
      dispatch(slice.actions.setNotifCounts(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error: error.message };
    }
  };
}

/**
 * Active les notifications pour tous les sites accessibles à l'utilisateur.
 *
 * Effectue un appel POST vers `/notifications/sites/enable-all/{userId}`.
 * Met à jour les comptes de notification via `getNotifSitesCounts` après succès.
 *
 * @param {number} userId - ID de l'utilisateur
 * @returns {Function} Fonction Redux async (thunk)
 */
export function enableNotifAllSites(userId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.post(`/notifications/sites/enable-all/${userId}`);
      dispatch(getNotifSitesCounts(userId));
      return result;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error: error.message };
    }
  };
}

/**
 * Désactive les notifications pour tous les sites accessibles à l'utilisateur.
 *
 * Effectue un appel POST vers `/notifications/sites/disable-all/{userId}`.
 * Met à jour les comptes de notification via `getNotifSitesCounts` après succès.
 *
 * @param {number} userId - ID de l'utilisateur
 * @returns {Function} Fonction Redux async (thunk)
 */
export function disableNotifAllSites(userId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.post(`/notifications/sites/disable-all/${userId}`);
      dispatch(getNotifSitesCounts(userId));
      return result;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error: error.message };
    }
  };
}
